import { useEffect, useState, useCallback } from "react";
import { Sparkles, Check, X } from "lucide-react";

// FirstStepsCard (Batch 3 / 3A) — surfaces the first-run recommendations that
// provision generates into os_proactive_suggestions (kind='first_run'). The
// recs already feed the Operator's context; this is their VISUAL home on the
// dashboard so the user sees concrete next steps on day one.
//
// - GET /api/proactive/suggestions returns ALL kinds (incl. passive-scanner
//   suggestions) — we filter to kind==='first_run' so nothing else leaks in.
// - accept/dismiss POST to /api/proactive/suggestions/respond (auth+ownership;
//   same-origin fetch sends the session cookie, like other authed tenant calls).
// - Renders NOTHING when there are no first_run suggestions (no empty card).

interface Suggestion {
  id: string;
  kind?: string;
  narration?: string;
  reason?: string;
  payload?: { cta?: string; providers?: string[]; objective?: string } | null;
}

export function FirstStepsCard({
  workspaceId,
  brandColor,
  onAskOperator,
}: {
  workspaceId?: string;
  brandColor?: string;
  onAskOperator?: (prompt: string) => void;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/proactive/suggestions?workspace_id=${encodeURIComponent(workspaceId)}`,
        );
        const json = await res.json();
        if (cancelled) return;
        const all: Suggestion[] = Array.isArray(json?.suggestions) ? json.suggestions : [];
        setSuggestions(
          all.filter((s) => s.kind === "first_run" && s.payload?.cta !== "desktop_agent_setup"),
        );
      } catch {
        // Non-blocking — the card simply doesn't render if this fails.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const respond = useCallback(
    async (id: string, action: "accept" | "dismiss") => {
      // Optimistic — remove immediately; the POST is best-effort.
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      try {
        await fetch("/api/proactive/suggestions/respond", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ suggestion_id: id, action }),
        });
      } catch {
        // Swallow — the suggestion is already gone from the UI; re-surfacing on
        // next load is acceptable and rare.
      }
    },
    [],
  );

  const accept = useCallback(
    async (suggestion: Suggestion) => {
      await respond(suggestion.id, "accept");
    },
    [respond],
  );

  // Render nothing until loaded and only when there are real first-run recs.
  if (!loaded || suggestions.length === 0) return null;

  const accent = brandColor || "#6366F1";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-center gap-2 mb-4">
        <span
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${accent}1a`, color: accent }}
        >
          <Sparkles className="w-4 h-4" strokeWidth={2} />
        </span>
        <h3 className="font-semibold text-slate-900">First steps</h3>
      </div>

      <ul className="flex flex-col gap-3">
        {suggestions.map((s) => (
          <li
            key={s.id}
            className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3.5"
          >
            <div className="flex-1 min-w-0">
              {s.narration && (
                <p className="text-sm font-medium text-slate-900 leading-snug">{s.narration}</p>
              )}
              {s.reason && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{s.reason}</p>}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                aria-label="Mark as done"
                onClick={() => accept(s)}
                style={{ backgroundColor: accent }}
                className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-white hover:opacity-90 transition-opacity"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                type="button"
                aria-label="Dismiss"
                onClick={() => respond(s.id, "dismiss")}
                className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
